import { useApolloClient } from '@apollo/client';
import {
  GetOriginalQuaysDocument,
  GetOriginalQuaysQuery,
  GetOriginalQuaysQueryVariables,
  GetStopPointsByQuayIdDocument,
  GetStopPointsByQuayIdQuery,
  GetStopPointsByQuayIdQueryVariables,
  ServicePatternScheduledStopPointInsertInput,
  ServicePatternScheduledStopPointSetInput,
  useInsertStopPointMutation,
  useMoveQuayToStopPlaceMutation,
  useUpdateStopPointMutation,
} from '../../../../../../generated/graphql';
import { KnownValueKey, areEqual, findKeyValue } from '../../../../../../utils';
import { tryToParseDate } from '../../../../../../utils/time';
import {
  MoveQuayParams,
  MoveStopPlace,
  QuayInfo,
  StopPointInfo,
} from './types';

export function extractStopPlaceQuays(
  stopPlace: MoveStopPlace | null | undefined,
): QuayInfo[] {
  // eslint-disable-next-line no-underscore-dangle
  if (stopPlace?.__typename !== 'stop_registry_StopPlace') {
    return [];
  }

  if (!stopPlace.quays) {
    return [];
  }

  return stopPlace.quays
    .filter((quay): quay is NonNullable<typeof quay> => quay !== null)
    .map((quay) => ({
      id: quay.id ?? '',
      publicCode: quay.publicCode ?? '',
      validityStart: tryToParseDate(
        findKeyValue(quay, KnownValueKey.ValidityStart),
      ),
    }))
    .filter((quay) => quay.id && quay.publicCode);
}

export function extractQuayValidityEnd(
  stopPlace: MoveStopPlace | null | undefined,
  quayId: string,
): Temporal.PlainDate | null | undefined {
  // eslint-disable-next-line no-underscore-dangle
  if (stopPlace?.__typename !== 'stop_registry_StopPlace') {
    return undefined;
  }

  if (!stopPlace.quays) {
    return undefined;
  }

  const quay = stopPlace.quays.find((q) => q?.id === quayId);
  if (!quay) {
    return undefined;
  }

  const validityEnd = findKeyValue(quay, KnownValueKey.ValidityEnd);
  return tryToParseDate(validityEnd);
}

export function createQuayMappingForCopiedQuay(
  originalQuays: ReadonlyArray<QuayInfo>,
  newQuays: ReadonlyArray<QuayInfo>,
  moveQuayFromDate: Temporal.PlainDate,
): ReadonlyMap<string, string> {
  const mapping = new Map<string, string>();

  originalQuays.forEach((originalQuay) => {
    if (!originalQuay?.id || !originalQuay?.publicCode) {
      return;
    }

    const matchingNewQuay = newQuays.find(
      (newQuay) =>
        newQuay.publicCode === originalQuay.publicCode &&
        areEqual(newQuay.validityStart, moveQuayFromDate),
    );

    if (matchingNewQuay?.id) {
      mapping.set(originalQuay.id, matchingNewQuay.id);
    }
  });

  return mapping;
}

export function useFetchOriginalQuays() {
  const apollo = useApolloClient();

  return async (quayIds: ReadonlyArray<string>): Promise<QuayInfo[]> => {
    const originalQuaysResult = await apollo.query<
      GetOriginalQuaysQuery,
      GetOriginalQuaysQueryVariables
    >({
      query: GetOriginalQuaysDocument,
      variables: { quayId: quayIds[0] },
    });

    const stopPlace = originalQuaysResult.data.stop_registry?.stopPlace?.at(0);
    const allQuaysInOriginalStop = extractStopPlaceQuays(stopPlace);

    return allQuaysInOriginalStop.filter((quay) =>
      quayIds.includes(quay?.id ?? ''),
    );
  };
}

export function useFetchExistingStopPoints() {
  const apollo = useApolloClient();

  return async (quayIds: ReadonlyArray<string>) => {
    const stopPointsResult = await apollo.query<
      GetStopPointsByQuayIdQuery,
      GetStopPointsByQuayIdQueryVariables
    >({
      query: GetStopPointsByQuayIdDocument,
      variables: { quayIds },
    });

    return stopPointsResult.data.service_pattern_scheduled_stop_point;
  };
}

export async function executeQuayMove(
  params: MoveQuayParams,
  moveQuayMutation: ReturnType<typeof useMoveQuayToStopPlaceMutation>[0],
) {
  const moveResult = await moveQuayMutation({
    variables: {
      toStopPlaceId: params.toStopPlaceId,
      quayIds: params.quayIds,
      moveQuayFromDate: params.moveQuayFromDate,
    },
  });

  const movedStopPlace = moveResult?.data?.stop_registry?.moveQuaysToStop;
  if (!movedStopPlace) {
    throw new Error('Move operation failed - no data returned');
  }

  return movedStopPlace;
}

export async function updateStopPointValidity(
  stopPointId: string,
  validityEnd: Temporal.PlainDate,
  updateStopPointMutation: ReturnType<typeof useUpdateStopPointMutation>[0],
): Promise<void> {
  const updateChanges: ServicePatternScheduledStopPointSetInput = {
    validity_end: validityEnd,
  };

  const result = await updateStopPointMutation({
    variables: {
      stopId: stopPointId,
      changes: updateChanges,
    },
  });

  if (result.errors && result.errors.length > 0) {
    throw new Error(
      `Stop point validity update failed: ${result.errors.map((e) => e.message).join(', ')}`,
    );
  }

  if (!result.data) {
    throw new Error('Stop point validity update returned no data');
  }
}

export async function createAndInsertStopPoint(
  originalStopPoint: StopPointInfo,
  newQuayId: string,
  moveFromDate: Temporal.PlainDate,
  validityEnd: Temporal.PlainDate | null | undefined,
  insertStopPointMutation: ReturnType<typeof useInsertStopPointMutation>[0],
) {
  const baseStopPoint: ServicePatternScheduledStopPointInsertInput = {
    priority: originalStopPoint.priority,
    direction: originalStopPoint.direction,
    label: originalStopPoint.label,
    timing_place_id: originalStopPoint.timing_place_id,
    validity_start: moveFromDate,
    validity_end: validityEnd ?? null,
    located_on_infrastructure_link_id:
      originalStopPoint.located_on_infrastructure_link_id,
    stop_place_ref: newQuayId,
    measured_location: originalStopPoint.measured_location,
  };

  // Add vehicle modes if they exist
  const vehicleModes = originalStopPoint.vehicle_mode_on_scheduled_stop_point;
  const stopPoint = vehicleModes?.length
    ? {
        ...baseStopPoint,
        vehicle_mode_on_scheduled_stop_point: {
          data: vehicleModes.map((vm) => ({
            vehicle_mode: vm.vehicle_mode,
          })),
        },
      }
    : baseStopPoint;

  await insertStopPointMutation({
    variables: {
      stopPoint,
    },
  });
}
